import { CommonModule } from '@angular/common';
import { Component, inject, input, signal, type InputSignal, type WritableSignal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import dayjs from 'dayjs';
import { AutoCompleteModule, type AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import { ReportCohortBasedOn, ReportCohortPeriod } from '../../../../../interfaces/reports';
import { ReportsService } from '../../../../../services/reports.service';
import { CardHeader } from '../../../../../shared/components/card/card-header/card-header';
import { Card } from '../../../../../shared/components/card/card/card';
import { EmptyChart } from '../../../../../shared/components/empty-chart/empty-chart';
import { ReportCohortPeriodPipe } from '../../../../../shared/pipes/report-cohort-period-pipe';
import { colorByPercentage } from '../../../../../shared/functions/color-by-percentage';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { SelectModule } from 'primeng/select';
import { ReportCohortBasedByPipe } from "../../../../../shared/pipes/report-cohort-based-by-pipe";
import { saveAs } from 'file-saver';
import { MenuModule } from 'primeng/menu';
import type { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-cohort-clients-depositors',
  imports: [
    CommonModule,
    FormsModule,
    Card,
    CardHeader,
    TableModule,
    SkeletonModule,
    ProgressSpinnerModule,
    DatePickerModule,
    ReactiveFormsModule,
    FloatLabelModule,
    ButtonModule,
    AutoCompleteModule,
    SelectButtonModule,
    ReportCohortPeriodPipe,
    TooltipModule,
    EmptyChart,
    SelectModule,
    ReportCohortBasedByPipe,
    MenuModule
],
  templateUrl: './cohort-clients-depositors.html',
  styleUrl: './cohort-clients-depositors.css',
})
export class CohortClientsDepositors {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _reportsService: ReportsService = inject(ReportsService);

  public exportLoading: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly reportCohortPeriod: typeof ReportCohortPeriod = ReportCohortPeriod;
  protected readonly reportCohortBasedOn: typeof ReportCohortBasedOn = ReportCohortBasedOn;

  public financialBrandUtmSourceData: InputSignal<any> = input<any>(null);

  public financialCohortClientsDepositorsShow: ReportCohortPeriod = ReportCohortPeriod.DAILY;

  public financialCohortClientsDepositorsUtmFilter: WritableSignal<string[]> = signal<string[]>([]);
  public financialCohortClientsDepositorsForm: FormGroup = this._fb.group({
    utm_source: [null],
    based_by: [ReportCohortBasedOn.CREATED],
    date_range_daily: [[dayjs().startOf('month').toDate(), dayjs().toDate()]],
    date_range_monthly: [[dayjs().startOf('year').toDate(), dayjs().toDate()]],
  });

  public financialCohortClientsDepositorsDailyData: WritableSignal<any> = signal<any>(null);
  public financialCohortClientsDepositorsDailyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortClientsDepositorsDailyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public financialCohortClientsDepositorsMonthlyData: WritableSignal<any> = signal<any>(null);
  public financialCohortClientsDepositorsMonthlyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortClientsDepositorsMonthlyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public exportMenuItems: MenuItem[] = [
    {
      label: 'PDF',
      icon: 'fa-solid fa-file-pdf',
      action: () => this.export()
    },
    {
      label: 'CSV',
      icon: 'fa-solid fa-file-csv',
      action: () => this.exportCsv()
    }
  ];

  get tableId(): string {
    if (this.financialCohortClientsDepositorsShow === ReportCohortPeriod.DAILY) {
      return 'financialCohortClientsDepositorsDailyTable';
    }
    return 'financialCohortClientsDepositorsMonthlyTable';
  }

  public filterUtmMethodCompleteMethod(event: AutoCompleteCompleteEvent): void {
    this.financialCohortClientsDepositorsUtmFilter.set(
      this.financialBrandUtmSourceData().utm_sources
        .filter((item: string) => item.toLowerCase().includes(event.query.toLowerCase()))
    )
  }

  public fromPaymentFiltersForm(form: FormGroup, key: string): string {

    if (key === 'start_date_daily') {
      return dayjs(form.get('date_range_daily')?.value[0]).format('YYYY-MM-DD');
    }

    if (key === 'end_date_daily') {
      return dayjs(form.get('date_range_daily')?.value[1]).format('YYYY-MM-DD');
    }

    if (key === 'start_date_monthly') {
      return dayjs(form.get('date_range_monthly')?.value[0]).format('YYYY-MM-DD');
    }

    if (key === 'end_date_monthly') {
      return dayjs(form.get('date_range_monthly')?.value[1]).format('YYYY-MM-DD');
    }

    return form.get(key)?.value;
  }

  public getFinancialCohortClientsDepositors(force: boolean = false): void {

    if (!force) {
      if (this.financialCohortClientsDepositorsShow === ReportCohortPeriod.DAILY
        && this.financialCohortClientsDepositorsDailyData()) {
        return;
      }

      if (this.financialCohortClientsDepositorsShow === ReportCohortPeriod.MONTHLY
        && this.financialCohortClientsDepositorsMonthlyData()
      ) {
        return;
      }
    }

    if (this.financialCohortClientsDepositorsShow === ReportCohortPeriod.DAILY) {
      this.getFinancialCohortClientsDepositorsDaily();
    }

    if (this.financialCohortClientsDepositorsShow === ReportCohortPeriod.MONTHLY) {
      this.getFinancialCohortClientsDepositorsMonthly();
    }
  }

  public getFinancialCohortClientsDepositorsDaily(): void {
    this.financialCohortClientsDepositorsDailyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'end_date_daily'),
    };

    this.financialCohortClientsDepositorsDailyData.set(null);
    this.financialCohortClientsDepositorsDailyFirstRequest.set(false);

    this._reportsService.financialCohortClientsDepositorsDaily(filters)
      .pipe(finalize(() => this.financialCohortClientsDepositorsDailyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortClientsDepositorsDailyData.set(response.data);
        }
      })
  }

  public getFinancialCohortClientsDepositorsMonthly(): void {
    this.financialCohortClientsDepositorsMonthlyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'end_date_monthly'),
    };

    this.financialCohortClientsDepositorsMonthlyData.set(null);
    this.financialCohortClientsDepositorsMonthlyFirstRequest.set(false);

    this._reportsService.financialCohortClientsDepositorsMonthly(filters)
      .pipe(finalize(() => this.financialCohortClientsDepositorsMonthlyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortClientsDepositorsMonthlyData.set(response.data);
        }
      })
  }

  public color(percentage: number) {
    return colorByPercentage(percentage);
  }

  public exportCsv(): void {
    if (this.financialCohortClientsDepositorsShow === ReportCohortPeriod.DAILY) {
      this.exportCsvDaily();
    } else if (this.financialCohortClientsDepositorsShow === ReportCohortPeriod.MONTHLY) {
      this.exportCsvMonthly();
    }
  }

  public exportCsvDaily(): void {
    this.exportLoading.update(value => !value);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'end_date_daily'),
    };

    this._reportsService.financialCohortClientsDepositorsDailyCsv(filters)
      .pipe(finalize(() => this.exportLoading.update(value => !value)))
      .subscribe({
        next: (blob) => {
          console.log({blob})
          const fileName = `cohort-clientes-depositantes-diario-${new Date().toLocaleString()}.csv`;
          saveAs(blob, fileName);
        },
      })
  }

  public exportCsvMonthly(): void {
    this.exportLoading.update(value => !value);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'end_date_monthly'),
    };

    this._reportsService.financialCohortClientsDepositorsMonthlyCsv(filters)
      .pipe(finalize(() => this.exportLoading.update(value => !value)))
      .subscribe({
        next: (blob) => {
          console.log({blob})
          const fileName = `cohort-clientes-depositantes-mensal-${new Date().toLocaleString()}.csv`;
          saveAs(blob, fileName);
        },
      })
  }

  public async export() {
    this.exportLoading.update(value => !value);
    const el = document.getElementById(this.tableId);

    const period = {
      start: this.financialCohortClientsDepositorsShow === ReportCohortPeriod.DAILY ?
        this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'start_date_daily') :
        this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'start_date_monthly'),
      end: this.financialCohortClientsDepositorsShow === ReportCohortPeriod.DAILY ?
        this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'end_date_daily') :
        this.fromPaymentFiltersForm(this.financialCohortClientsDepositorsForm, 'end_date_monthly'),
    }

    if (el) {
      setTimeout(async () => {
        await html2canvas(el).then(canvas => {
          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('p');
          const imgWidth = 200;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          pdf.setFontSize(12);
          pdf.text("Cohort - Clientes Depositantes", 5, 10);
          pdf.text(`${dayjs(period.start).format('DD/MM/YYYY')} - ${dayjs(period.end).format('DD/MM/YYYY')}`, 205, 10, { align: 'right' });
          pdf.addImage(imgData, 'PNG', 5, 15, imgWidth, imgHeight);
          pdf.save(`${this.tableId}-${dayjs().format('YYYY-MM-DD-HH:mm:ss')}.pdf`);

          this.exportLoading.update(value => !value);
        });
      });
    }
  }
}

